/**
 * Super-admin tenant switcher (premium-content workstream): a locally stored
 * tenant id that overrides the `X-Tenant-Id` header on EVERY request. Only the
 * AppHeader switcher (rendered for platform users with `tenants_read`) writes
 * it; the backend additionally rejects cross-tenant requests for anyone but
 * `platform_super_admin`, so a stale/forged value cannot escalate anything.
 */
const STORAGE_KEY = 'mfw.tenantOverride';

export function getTenantOverride(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setTenantOverride(tenantId: string | null) {
  try {
    if (tenantId) window.localStorage.setItem(STORAGE_KEY, tenantId);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable — the switcher simply won't persist
  }
}

/**
 * Fires when ANOTHER tab changes the override (the browser dispatches
 * `storage` only in the tabs that did not write). A `key` of `null` is
 * `localStorage.clear()` — the override may be among what vanished, so it
 * counts.
 *
 * Exists because the override retargets `X-Tenant-Id` on every request the
 * moment it is written, in every tab — but only the writing tab reloads
 * (`TenantSwitcher`). An unnotified tab keeps rendering one tenant while its
 * requests silently carry another: on `/admin/rebrand` that is an editor
 * seeded from tenant X saving colours into tenant Y.
 */
export function subscribeTenantOverrideChanged(onChange: () => void) {
  const listener = (event: StorageEvent) => {
    if (event.key === null || event.key === STORAGE_KEY) onChange();
  };
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}

/**
 * THE tenant a request acts on: the stored override, else the DOMAIN tenant.
 *
 * Lives here, beside the override itself, because every reader of the
 * override must agree on what counts as one: `api/axios.ts` builds
 * `X-Tenant-Id` from it, `/admin/rebrand` picks the tenant it seeds its
 * editors from with it, and the header `TenantSwitcher` shows which override
 * stands with it. Every branding route resolves its target from that header,
 * so a second implementation of this rule anywhere is a page that reads one
 * tenant and writes another — or a switcher that displays an override the
 * requests ignore.
 *
 * `||`, NOT `??`. Both sources can hand over an empty string — `localStorage`
 * holds whatever was written into it, and `mapTenantFromApi` defaults a missing
 * id to `''` — and `''` is the one shape every caller downstream mishandles: it
 * is defined, so `??` treats it as an answer and it shadows the domain tenant,
 * but it is falsy, so a query keyed on it is `enabled: false` and never fetches
 * while an `!tenantId` guard renders nothing. Absent is the honest reading of
 * an empty id, and `undefined` is what callers already handle.
 */
export const resolveActingTenantId = (
  override: null | string,
  domainTenantId?: null | string,
) => override || domainTenantId || undefined;
